import { useState, useEffect } from 'react'
import PageNavigation from '../../../components/PageNavigation/PageNavigation'
import './FAQ.css'

const FAQ = () => {
  const [activeSection, setActiveSection] = useState('general')

  const sections = [
    { id: 'general', title: 'General' },
    { id: 'deployment', title: 'Deployment' },
    { id: 'billing', title: 'Billing' },
    { id: 'support', title: 'Support' }
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 200

      for (let i = sections.length - 1; i >= 0; i--) {
        const element = document.getElementById(`faq-${sections[i].id}`)
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id)
          break
        }
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [sections])

  return (
    <div className="faq-page">
      <div className="faq-content-wrapper">
        <div className="faq-main-content">
          <h1>Frequently Asked Questions</h1>
          <p className="intro">
            Find answers to common questions about Aedify.AI platform and services.
          </p>

          <section id="faq-general" className="faq-section">
            <h2>General</h2>
            <div className="faq-item">
              <h3>What is Aedify.AI?</h3>
              <p>
                Aedify.AI is a cloud platform that simplifies application deployment, infrastructure 
                management, and cloud services. It provides tools for deploying applications, managing 
                databases, storing files, and running AI inference workloads.
              </p>
            </div>
            <div className="faq-item">
              <h3>How do I get started?</h3>
              <p>
                Sign up for an account, follow the <a href="/quick-start">Quick Start guide</a>, and 
                deploy your first application. The platform guides you through each step.
              </p>
            </div>
            <div className="faq-item">
              <h3>What programming languages are supported?</h3>
              <p>
                Aedify.AI supports any language that can run in a container. Popular options include 
                Node.js, Python, Go, Ruby, Java, and more.
              </p>
            </div>
          </section>

          <section id="faq-deployment" className="faq-section">
            <h2>Deployment</h2>
            <div className="faq-item">
              <h3>How do I deploy from GitHub?</h3>
              <p>
                Connect your GitHub account, select your repository, choose a branch, and click deploy. 
                Aedify.AI automatically detects your stack and builds your application. See the 
                <a href="/platform-capabilities/apps"> Apps documentation </a> for detailed steps.
              </p>
            </div>
            <div className="faq-item">
              <h3>Can I deploy from other Git providers?</h3>
              <p>
                Currently, GitHub is supported. Support for other Git providers is coming soon.
              </p>
            </div>
            <div className="faq-item">
              <h3>How long does deployment take?</h3>
              <p>
                Deployment time varies based on your application size and build complexity. Most 
                applications deploy in 2-5 minutes.
              </p>
            </div>
          </section>

          <section id="faq-billing" className="faq-section">
            <h2>Billing</h2>
            <div className="faq-item">
              <h3>How is pricing calculated?</h3>
              <p>
                Pricing is based on resource usage including compute hours, storage, bandwidth, and 
                database usage. You only pay for what you use.
              </p>
            </div>
            <div className="faq-item">
              <h3>Do you offer a free tier?</h3>
              <p>
                Yes, we offer a free tier with limited resources. Check the <a href="/account/billing">Billing </a> 
                page for current pricing and free tier limits.
              </p>
            </div>
            <div className="faq-item">
              <h3>How do I view my usage?</h3>
              <p>
                Navigate to the <a href="/account/usage">Usage</a> section to view real-time and 
                historical usage data.
              </p>
            </div>
          </section>

          <section id="faq-support" className="faq-section">
            <h2>Support</h2>
            <div className="faq-item">
              <h3>How do I get help?</h3>
              <p>
                You can reach out through the in-app support, email <a href="mailto:support@aedify.ai">support@aedify.ai</a>, 
                or visit our <a href="/resources/support">Support</a> page for more options.
              </p>
            </div>
            <div className="faq-item">
              <h3>Is there a community forum?</h3>
              <p>
                Yes! Join our <a href="/resources/community">Community</a> to connect with other users, 
                ask questions, and share knowledge.
              </p>
            </div>
          </section>

          <PageNavigation />
        </div>

        {/* Right Sidebar - Section Navigation */}
        <aside className="faq-sidebar">
          <div className="sidebar-section">
            <h3>Contents</h3>
            <nav className="section-nav">
              {sections.map((section) => (
                <a
                  key={section.id}
                  href={`#faq-${section.id}`}
                  className={`section-nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={(e) => {
                    e.preventDefault()
                    setActiveSection(section.id)
                    document.getElementById(`faq-${section.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                  }}
                >
                  {section.title}
                </a>
              ))}
            </nav>
          </div>
        </aside>
      </div>
    </div>
  )
}

export default FAQ
